<template>
    <div v-if="numberOfPages > 1" class="tw-flex tw-items-center tw-justify-center">
        <button
            type="button"
            class="tw-p-2"
            :class="{ 'tw-text-slate-200': !isPrevBtnActive, 'tw-cursor-pointer': isPrevBtnActive }"
            :disabled="!isPrevBtnActive"
            @click="handleClick(currentPage - 1)"
        >
            <svg
                xmlns="http://www.w3.org/2000/svg"
                class="tw-h-6 tw-w-6"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
                stroke-width="2"
            >
                <path stroke-linecap="round" stroke-linejoin="round" d="M15 19l-7-7 7-7" />
            </svg>
        </button>
        <div class="tw-flex tw-items-center">
            <button
                type="button"
                class="tw-block tw-px-3 tw-py-1 hover:tw-text-brand-primary tw-cursor-pointer"
                :class="{
                    'tw-shadow-[0_2px_0] tw-shadow-brand-primary': isPageActive(firstPage),
                }"
                @click="handleClick(firstPage)"
            >
                {{ firstPage }}
            </button>
            <span
                v-if="!shouldHidePlaceholders && shouldShowAfterPlaceholder"
                class="tw-block tw-px-3 tw-py-1 tw-text-slate-300"
            >
                &hellip;
            </span>
            <button
                v-for="(pageItem, i) in visiblePages"
                :key="i"
                type="button"
                class="tw-block tw-px-3 tw-py-1 hover:tw-text-brand-primary tw-cursor-pointer"
                :class="{
                    'tw-shadow-[0_2px_0] tw-shadow-brand-primary': isPageActive(pageItem),
                }"
                @click="handleClick(pageItem)"
            >
                {{ pageItem }}
            </button>
            <span
                v-if="!shouldHidePlaceholders && shouldShowBeforePlaceholder"
                class="tw-block tw-px-3 tw-py-1 tw-text-slate-300"
            >
                &hellip;
            </span>
            <button
                type="button"
                class="tw-block tw-px-3 tw-py-1 hover:tw-text-brand-primary tw-cursor-pointer"
                :class="{
                    'tw-shadow-[0_2px_0] tw-shadow-brand-primary': isPageActive(numberOfPages),
                }"
                @click="handleClick(numberOfPages)"
            >
                {{ numberOfPages }}
            </button>
        </div>
        <button
            type="button"
            class="tw-p-2"
            :class="{ 'tw-text-slate-200': !isNextBtnActive, 'tw-cursor-pointer': isNextBtnActive }"
            :disabled="!isNextBtnActive"
            @click="handleClick(currentPage + 1)"
        >
            <svg
                xmlns="http://www.w3.org/2000/svg"
                class="tw-h-6 tw-w-6"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
                stroke-width="2"
            >
                <path stroke-linecap="round" stroke-linejoin="round" d="M9 5l7 7-7 7" />
            </svg>
        </button>
    </div>
</template>

<script>
/**
 * A component for displaying pagination.
 *
 * @version 1.0.0
 * @example ./../examples/DkPagination.md
 */
export default {
    name: 'DkPagination',
    props: {
        /**
         * Number of entities to show per page.
         */
        perPage: {
            type: [Number, String],
            default: 9,
        },
        /**
         * Total number of entities
         */
        totalEntities: {
            type: [Number, String],
            required: true,
        },
        /**
         * Current page number
         */
        page: {
            type: Number,
            default: 1,
        },
    },
    data() {
        return {
            currentPage: 1,
            firstPage: 1,
            pageShift: 4,
        };
    },
    computed: {
        numberOfPages() {
            const perPage = parseInt(this.perPage, 10);
            const totalEntities = parseInt(this.totalEntities, 10);

            if (perPage < 1 || totalEntities <= 1) {
                return 0;
            }

            return Math.ceil(totalEntities / perPage);
        },
        isPrevBtnActive() {
            return this.currentPage !== 1;
        },
        isNextBtnActive() {
            return this.currentPage !== parseInt(this.numberOfPages, 10);
        },
        shouldHidePlaceholders() {
            if (this.numberOfPages <= this.pageShift) {
                return true;
            }

            return false;
        },
        shouldShowAfterPlaceholder() {
            if (this.currentPage < this.pageShift) {
                return false;
            }

            return true;
        },
        shouldShowBeforePlaceholder() {
            if (this.currentPage > this.numberOfPages - (this.pageShift - 1)) {
                return false;
            }

            return true;
        },
        visiblePages() {
            const result = [];

            if (this.shouldHidePlaceholders) {
                for (let i = this.firstPage + 1; i < this.numberOfPages; i += 1) {
                    result.push(i);
                }

                return result;
            }
            if (this.currentPage < this.pageShift) {
                for (let i = this.firstPage + 1; i <= this.pageShift; i += 1) {
                    result.push(i);
                }

                return result;
            }
            if (this.currentPage > this.numberOfPages - (this.pageShift - 1)) {
                for (let i = this.numberOfPages - (this.pageShift - 1); i < this.numberOfPages; i += 1) {
                    result.push(i);
                }
                return result;
            }

            return [this.currentPage - 1, this.currentPage, this.currentPage + 1];
        },
    },
    watch: {
        page(pageNumber) {
            this.$set(this, 'currentPage', pageNumber);
        },
    },
    created() {
        this.currentPage = this.page;
    },
    methods: {
        handleClick(page) {
            const currentPage = parseInt(page, 10);

            if (isNaN(currentPage)) {
                return;
            }

            if (currentPage < 1 || currentPage > this.numberOfPages) {
                return;
            }

            if (currentPage === this.currentPage) {
                return;
            }

            this.currentPage = currentPage;

            /**
             * Fired when the current page is changed by pagination navigation.
             *
             * @event paging-clicked
             * @type {number}
             */
            this.$emit('paging-clicked', this.currentPage);
        },
        isPageActive(page) {
            const currentItem = parseInt(page, 10);

            if (isNaN(currentItem)) {
                return false;
            }

            return currentItem === this.currentPage;
        },
    },
};
</script>
